import { Input, Option, TableRow } from '@sam/components';
import { NavigateFunction } from 'react-router-dom';
import i18n from '../../../client/src/i18n/i18n';
import { Language } from '../Language/Language.types';
import { Office } from '../office/Office.types';
import {
  InventoryCategory,
  InventoryEntry,
  InventoryGroup,
  ToolingGroup,
  ToolingType,
} from './Inventory.types';

/**
 * Util method to create an inventoryEntry
 * @param group the inventoryGroup of the entry
 * @param override partial entry to adjust created instance
 * @returns  Created inventoryEntry
 */
export const generateNewInventoryEntry = (
  group: InventoryGroup,
  override?: Partial<InventoryEntry>,
): InventoryEntry => {
  return {
    metaInformations: new Map<string, string>(),
    note: '',
    producent: '',
    serialNumber: '',
    reminderMail: false,
    createDate: new Date(),
    createdBy: undefined!,
    id: undefined!,
    name: '',
    number: 0,
    officeId: '',
    checks: [],
    categoryId: '',
    currentUserId: '',
    userChanges: [],
    disabled: false,
    group,
    ...override,
  };
};

/**
 * Helper to create dropdown options for inventory tooling groups
 *
 * @returns The dropdown options
 */
export const generateDropdownOptionsForToolingGroups = (): Option[] => {
  return Object.keys(ToolingGroup).map((group) => ({
    label: i18n.t(`pages.inventory.toolingGroup.${group}`),
    value: group,
  }));
};

/**
 * Helper to create dropdown options for inventory tooling types
 *
 * @returns The dropdown options
 */
export const generateDropdownOptionsForToolingTypes = (): Option[] => {
  return Object.keys(ToolingType).map((toolType) => ({
    label: i18n.t(`pages.inventory.toolingType.${toolType}`),
    value: toolType,
  }));
};

/**
 * Util method to convert inventoryEntries into TableEntries
 * @param items to display
 * @param navigate navigateFunction to go to edit
 * @param selectedInventoryType to show
 * @param offices list of all Offices to display the office for the items
 * @returns Array of TableRows
 */
export const convertInventoryEntriesIntoTableEntries = (
  items: InventoryEntry[],
  categories: InventoryCategory[],
  navigate: NavigateFunction,
  offices: Office[],
): TableRow[] => {
  return items.map((entry) => ({
    id: entry.id,
    onClick: () => navigate('/inventory/edit', { state: { item: entry } }),
    content: [
      entry.name,
      categories.find((cat) => cat.id === entry.categoryId)?.title || '-',
      entry.number.toString().padStart(5, '0'),
      offices.find((office) => office.id === entry.officeId)?.name || '-',
      entry.disabled ? i18n.t('pages.inventory.deactivated') : i18n.t('pages.inventory.active'),
    ],
  }));
};

/**
 * Util method to generate tableRows for translations
 * @param translations the translations map containing the data to display
 * @param update update method will be called onChange
 * @returns Array containing a row for each language
 */
export const generateTranslationTableRows = (
  translations: Map<Language, string>,
  update: (language: Language, value: string) => void,
): TableRow[] => {
  const rows: TableRow[] = [];
  Object.values(Language).forEach((language) =>
    rows.push({
      id: 'translation-row ' + language,
      content: [
        <p>{i18n.t(`general.languages.${language}`)}</p>,
        <Input
          value={translations.get(language)}
          type="text"
          onChange={(translation) => update(language, translation)}
        />,
      ],
    }),
  );
  return rows;
};
